import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell,
} from "recharts";
import {
  Trophy, Target, Clock, Flame, TrendingUp, TrendingDown,
  ArrowRight, BookOpen, Star, Zap, Crown, Compass,
  CalendarCheck, Rocket, Loader2, ArrowLeft, Minus,
  AlertTriangle, CheckCircle, Lightbulb, Award,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getSessionId = () => {
  let sid = sessionStorage.getItem("quiz_session_id");
  if (!sid) { sid = Math.random().toString(36).slice(2); sessionStorage.setItem("quiz_session_id", sid); }
  return sid;
};

const ICON_MAP = {
  rocket: Rocket, flame: Flame, crown: Crown, star: Star, zap: Zap,
  compass: Compass, "trending-up": TrendingUp, trophy: Trophy,
  "calendar-check": CalendarCheck,
};

export default function MyProgressPage() {
  const { user, token } = useAuth();
  const [summary, setSummary] = useState(null);
  const [history, setHistory] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [badges, setBadges] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newBadge, setNewBadge] = useState(null);

  useEffect(() => {
    fetchAll();
  }, [user]);

  const fetchAll = async () => {
    setLoading(true);
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const sid = getSessionId();
    const qs = user ? "" : `?session_id=${sid}`;
    try {
      const [sumRes, histRes, subRes, badgeRes, recRes] = await Promise.all([
        axios.get(`${API}/quiz-progress/summary${qs}`, { headers }),
        axios.get(`${API}/quiz-progress/history${qs}`, { headers }),
        axios.get(`${API}/quiz-progress/subjects${qs}`, { headers }),
        axios.get(`${API}/quiz-progress/badges${qs}`, { headers }),
        axios.get(`${API}/quiz-progress/recommendations${qs}`, { headers }),
      ]);
      setSummary(sumRes.data);
      setHistory(histRes.data);
      setSubjects(subRes.data);
      setBadges(badgeRes.data);
      setRecommendations(recRes.data);
    } catch (e) { console.error("Failed to fetch progress:", e); }
    finally { setLoading(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-slate-50 to-white">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#e0482d] mx-auto mb-3" />
          <p className="text-sm text-slate-500">Loading your progress...</p>
        </div>
      </div>
    );
  }

  const hasData = summary && summary.total_quizzes > 0;
  const trendIcon = summary?.recent_trend === "up" ? <TrendingUp className="w-4 h-4 text-emerald-500" /> : summary?.recent_trend === "down" ? <TrendingDown className="w-4 h-4 text-red-500" /> : <Minus className="w-4 h-4 text-slate-400" />;
  const trendLabel = summary?.recent_trend === "up" ? "Improving" : summary?.recent_trend === "down" ? "Declining" : "Steady";

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="my-progress-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
          <Link to="/sample-preview" className="inline-flex items-center gap-2 text-slate-400 hover:text-white text-sm mb-4 transition-colors" data-testid="back-link">
            <ArrowLeft className="w-4 h-4" /> Back to Quizzes
          </Link>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#e0482d] to-[#ff6b4a] flex items-center justify-center shadow-lg shadow-[#e0482d]/20">
              <Trophy className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold">My Progress</h1>
              <p className="text-slate-400 text-sm">
                {user ? `Welcome back, ${user.name || "learner"}!` : "Your quiz journey — sign up to save progress permanently"}
              </p>
            </div>
          </div>
          {!user && (
            <div className="mt-4 inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/20 text-amber-300 text-xs px-3 py-1.5 rounded-full" data-testid="save-progress-notice">
              <AlertTriangle className="w-3 h-3" />
              Progress is session-based. <Link to="/register" className="underline font-medium">Sign up</Link> to save it permanently.
            </div>
          )}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 -mt-4">
        {!hasData ? (
          /* Empty State */
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <Card className="rounded-2xl border-0 shadow-lg text-center" data-testid="empty-progress">
              <CardContent className="py-16">
                <div className="w-20 h-20 rounded-3xl bg-[#e0482d]/10 flex items-center justify-center mx-auto mb-5">
                  <BookOpen className="w-9 h-9 text-[#e0482d]" />
                </div>
                <h2 className="text-xl font-bold text-slate-900 mb-2">No quiz data yet</h2>
                <p className="text-sm text-slate-500 mb-6 max-w-sm mx-auto">
                  Take your first quiz to start tracking your progress, earn badges, and get personalized study recommendations.
                </p>
                <Button asChild className="bg-[#e0482d] hover:bg-[#c93d25] text-white rounded-xl h-11 px-6">
                  <Link to="/sample-preview" data-testid="start-quiz-link">
                    <BookOpen className="w-4 h-4 mr-2" /> Take a Quiz <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        ) : (
          <div className="space-y-6">
            {/* Stats Row */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { label: "Quizzes Taken", value: summary.total_quizzes, icon: Target, color: "#3b82f6", bg: "bg-blue-50" },
                { label: "Average Score", value: `${summary.avg_score}%`, icon: Trophy, color: "#10b981", bg: "bg-emerald-50" },
                { label: "Best Score", value: `${summary.best_score}%`, icon: Star, color: "#eab308", bg: "bg-yellow-50" },
                { label: "Subjects", value: summary.subjects_tried, icon: BookOpen, color: "#8b5cf6", bg: "bg-purple-50" },
              ].map((stat, i) => (
                <Card key={stat.label} className="rounded-2xl border-0 shadow-sm overflow-hidden" data-testid={`stat-${i}`}>
                  <CardContent className="p-4">
                    <div className={`w-9 h-9 rounded-xl ${stat.bg} flex items-center justify-center mb-2`}>
                      <stat.icon className="w-4.5 h-4.5" style={{ color: stat.color }} />
                    </div>
                    <p className="text-xl sm:text-2xl font-bold text-slate-900">{stat.value}</p>
                    <p className="text-xs text-slate-500">{stat.label}</p>
                  </CardContent>
                </Card>
              ))}
            </motion.div>

            {/* Streak + Trend */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
              className="grid grid-cols-2 gap-3">
              <Card className="rounded-2xl border-0 shadow-sm" data-testid="streak-card">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center shadow-md">
                    <Flame className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{summary.current_streak} day{summary.current_streak !== 1 ? "s" : ""}</p>
                    <p className="text-xs text-slate-500">Current Streak</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="rounded-2xl border-0 shadow-sm" data-testid="trend-card">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow-md ${
                    summary.recent_trend === "up" ? "bg-gradient-to-br from-emerald-500 to-green-500" :
                    summary.recent_trend === "down" ? "bg-gradient-to-br from-red-500 to-rose-500" :
                    "bg-gradient-to-br from-slate-400 to-slate-500"
                  }`}>
                    {summary.recent_trend === "up" ? <TrendingUp className="w-6 h-6 text-white" /> :
                     summary.recent_trend === "down" ? <TrendingDown className="w-6 h-6 text-white" /> :
                     <Minus className="w-6 h-6 text-white" />}
                  </div>
                  <div>
                    <p className="text-lg font-bold text-slate-900">{trendLabel}</p>
                    <p className="text-xs text-slate-500">Recent Trend</p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Score History Chart */}
            {history.length > 1 && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
                <Card className="rounded-2xl border-0 shadow-sm" data-testid="history-chart">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-[#e0482d]" /> Score History
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={240}>
                      <LineChart data={history} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                        <defs>
                          <linearGradient id="scoreGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#e0482d" stopOpacity={0.15} />
                            <stop offset="95%" stopColor="#e0482d" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                        <XAxis
                          dataKey="created_at"
                          tick={{ fontSize: 10, fill: "#94a3af" }}
                          tickFormatter={d => {
                            const dt = new Date(d);
                            return `${dt.getDate()}/${dt.getMonth() + 1}`;
                          }}
                        />
                        <YAxis tick={{ fontSize: 10, fill: "#94a3af" }} domain={[0, 100]} tickFormatter={v => `${v}%`} />
                        <Tooltip
                          contentStyle={{ borderRadius: "12px", border: "none", boxShadow: "0 4px 20px rgba(0,0,0,0.1)", fontSize: "12px" }}
                          formatter={(v, name) => [`${v}%`, "Score"]}
                          labelFormatter={d => {
                            const idx = history.findIndex(h => h.created_at === d);
                            const item = history[idx];
                            return item ? `${item.mock_title || item.subject}` : "";
                          }}
                        />
                        <Line type="monotone" dataKey="percentage" stroke="#e0482d" strokeWidth={2.5}
                          dot={{ fill: "#e0482d", r: 4, strokeWidth: 2, stroke: "#fff" }}
                          activeDot={{ r: 6, fill: "#e0482d" }}
                          fill="url(#scoreGrad)" />
                      </LineChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {/* Subject Breakdown */}
            {subjects.length > 0 && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
                <Card className="rounded-2xl border-0 shadow-sm" data-testid="subject-breakdown">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <BookOpen className="w-4 h-4 text-[#e0482d]" /> Subject Performance
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <ResponsiveContainer width="100%" height={subjects.length * 48 + 20}>
                        <BarChart data={subjects} layout="vertical" margin={{ top: 0, right: 20, left: 0, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                          <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 10, fill: "#94a3af" }} tickFormatter={v => `${v}%`} />
                          <YAxis type="category" dataKey="subject" tick={{ fontSize: 11, fill: "#475569" }} width={100} />
                          <Tooltip
                            contentStyle={{ borderRadius: "12px", border: "none", boxShadow: "0 4px 20px rgba(0,0,0,0.1)", fontSize: "12px" }}
                            formatter={(v) => [`${v}%`, "Avg Score"]}
                          />
                          <Bar dataKey="avg_score" radius={[0, 8, 8, 0]} maxBarSize={28}>
                            {subjects.map((s, i) => (
                              <Cell key={i} fill={s.avg_score >= 80 ? "#10b981" : s.avg_score >= 60 ? "#3b82f6" : "#ef4444"} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                      <div className="space-y-2">
                        {subjects.map((s) => (
                          <div key={s.subject} className="flex items-center justify-between p-3 rounded-xl bg-slate-50" data-testid={`subject-${s.subject}`}>
                            <div>
                              <p className="text-sm font-medium text-slate-700">{s.subject}</p>
                              <p className="text-xs text-slate-400">{s.attempts} attempt{s.attempts !== 1 ? "s" : ""} · Best: {s.best_score}%</p>
                            </div>
                            <div className="text-right">
                              <p className={`text-lg font-bold ${s.avg_score >= 80 ? "text-emerald-600" : s.avg_score >= 60 ? "text-blue-600" : "text-red-600"}`}>
                                {s.avg_score}%
                              </p>
                              <p className="text-[10px] text-slate-400">avg</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {/* Badges */}
            {badges && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
                <Card className="rounded-2xl border-0 shadow-sm" data-testid="badges-section">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Award className="w-4 h-4 text-amber-500" /> Badges
                      </CardTitle>
                      <Badge variant="secondary" className="text-xs">{badges.earned_count}/{badges.total_count} earned</Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-9 gap-3">
                      {badges.badges.map((b) => {
                        const IconComp = ICON_MAP[b.icon] || Star;
                        return (
                          <motion.div
                            key={b.key}
                            whileHover={{ scale: 1.08 }}
                            className={`flex flex-col items-center p-3 rounded-2xl text-center transition-all cursor-default ${
                              b.earned
                                ? "bg-gradient-to-b from-amber-50 to-orange-50 border-2 border-amber-200 shadow-sm"
                                : "bg-slate-50 border-2 border-slate-100 opacity-40 grayscale"
                            }`}
                            data-testid={`badge-${b.key}`}
                            title={b.description}
                          >
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-1.5 ${
                              b.earned ? "" : "bg-slate-200"
                            }`} style={b.earned ? { backgroundColor: `${b.color}15` } : {}}>
                              <IconComp className="w-5 h-5" style={{ color: b.earned ? b.color : "#94a3b8" }} />
                            </div>
                            <p className={`text-[10px] font-semibold leading-tight ${b.earned ? "text-slate-800" : "text-slate-400"}`}>
                              {b.name}
                            </p>
                            {b.earned && (
                              <CheckCircle className="w-3 h-3 text-emerald-500 mt-1" />
                            )}
                          </motion.div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {/* Recommendations */}
            {recommendations.length > 0 && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}>
                <Card className="rounded-2xl border-0 shadow-sm" data-testid="recommendations-section">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Lightbulb className="w-4 h-4 text-amber-500" /> Study Recommendations
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {recommendations.map((rec, i) => (
                      <div key={i}
                        className={`flex items-start gap-3 p-3 rounded-xl border ${
                          rec.priority === "high" ? "bg-red-50 border-red-200" :
                          rec.priority === "medium" ? "bg-amber-50 border-amber-200" :
                          rec.priority === "low" ? "bg-emerald-50 border-emerald-200" :
                          "bg-blue-50 border-blue-200"
                        }`}
                        data-testid={`rec-${i}`}
                      >
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                          rec.priority === "high" ? "bg-red-100" :
                          rec.priority === "medium" ? "bg-amber-100" :
                          rec.priority === "low" ? "bg-emerald-100" :
                          "bg-blue-100"
                        }`}>
                          {rec.priority === "high" ? <AlertTriangle className="w-4 h-4 text-red-600" /> :
                           rec.priority === "medium" ? <TrendingUp className="w-4 h-4 text-amber-600" /> :
                           rec.priority === "low" ? <CheckCircle className="w-4 h-4 text-emerald-600" /> :
                           <Lightbulb className="w-4 h-4 text-blue-600" />}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-slate-800">{rec.message}</p>
                          {rec.subject && <p className="text-xs text-slate-500 mt-0.5">Subject: {rec.subject} · Avg: {rec.avg_score}%</p>}
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {/* CTA */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}
              className="text-center py-4">
              <Button asChild className="bg-[#e0482d] hover:bg-[#c93d25] text-white rounded-xl h-11 px-6">
                <Link to="/sample-preview" data-testid="take-another-quiz">
                  <BookOpen className="w-4 h-4 mr-2" /> Take Another Quiz <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
            </motion.div>
          </div>
        )}
      </div>

      {/* Badge Toast */}
      <AnimatePresence>
        {newBadge && (
          <motion.div initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 50 }}
            className="fixed bottom-6 right-6 bg-gradient-to-r from-amber-500 to-orange-500 text-white px-5 py-3 rounded-2xl shadow-xl flex items-center gap-3 z-50" data-testid="badge-toast">
            <Award className="w-6 h-6" />
            <div>
              <p className="text-xs opacity-80">Badge Earned!</p>
              <p className="font-bold text-sm">{newBadge}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
